import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Globe, ChevronDown, Check, Phone, ChevronRight } from "lucide-react";
import { WhatsAppIcon } from "./PhoneLinks";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, CONTACT } from "../data/content";
import { useLang, LANGS } from "../i18n";
import { Logo } from "./Logo";

export const LanguageSwitcher = ({ className = "", align = "right", idPrefix = "" }) => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`} data-testid={`${idPrefix}language-switcher`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.common.language}
        data-testid={`${idPrefix}language-dropdown-toggle`}
        className="flex min-h-[44px] items-center gap-2 border border-[#C8D3DB] glass px-3 text-xs font-semibold tracking-wide text-[#0E1B24] transition-colors hover:border-[#0F5C8C]"
      >
        <Globe size={14} className="text-[#0F5C8C]" />
        <span className="font-mono2">{current.label}</span>
        <span className="hidden text-[#41535F] sm:inline">{current.name}</span>
        <ChevronDown size={14} className={`text-[#5E7280] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            data-testid={`${idPrefix}language-menu`}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className={`absolute z-50 mt-2 w-52 border border-[#C8D3DB] bg-[#FFFFFF]/95 p-1 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl ${align === "right" ? "right-0" : "left-0"}`}
          >
            {LANGS.map((l) => (
              <li key={l.code} role="option" aria-selected={lang === l.code}>
                <button
                  type="button"
                  data-testid={`${idPrefix}lang-${l.code}-option`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`flex w-full min-h-[44px] items-center gap-3 px-3 text-left text-sm transition-colors hover:bg-white/10 ${lang === l.code ? "text-[#0F5C8C]" : "text-[#0E1B24]"}`}
                >
                  <span className="w-7 font-mono2 text-[11px] text-[#5E7280]">{l.label}</span>
                  <span className="flex-1">{l.name}</span>
                  {lang === l.code && <Check size={14} />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const NavDropdown = ({ item, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  const active = item.children.some((c) => pathname === c.path);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`nav-${item.id}-toggle`}
        className={`flex items-center gap-1.5 whitespace-nowrap py-2 text-[13px] font-medium tracking-wide transition-colors hover:text-[#1477B3] ${active ? "text-[#0F5C8C]" : "text-[#41535F]"}`}
      >
        {t.nav[item.id]}
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            data-testid={`nav-${item.id}-menu`}
            className="absolute left-1/2 z-[60] w-[26rem] -translate-x-1/2 border border-[#0A3E63] bg-[#0E1B24] p-2 shadow-[0_30px_80px_-24px_rgba(14,27,36,0.65)]"
          >
            {item.children.map((c, i) => (
              <Link
                key={c.id}
                to={c.path}
                onClick={() => setOpen(false)}
                data-testid={`nav-${c.id}-link`}
                className="group flex items-start gap-4 p-4 transition-colors hover:bg-white/[0.08]"
              >
                <span className="mt-1 font-mono2 text-[11px] text-[#6FB6E0]">{`0${i + 1}`}</span>
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-display text-sm font-bold text-[#FFFFFF]">
                    {t.nav[c.id]}
                    <ArrowRight size={14} className="text-[#6FB6E0] opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-[#B9C6CE]">{t.navDesc[c.id]}</span>
                </span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle("nv-menu-open", open);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.classList.remove("nv-menu-open"); };
  }, [open]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const iconBtn = "flex h-11 w-11 items-center justify-center text-[#0E1B24] transition-colors hover:text-[#1477B3] xl:hidden";

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b bg-[#F4F6F8]/80 backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "border-[#C8D3DB]" : "border-[#DDE4EA]/80"
      }`}
    >
      <div className="mx-auto max-w-[1760px] px-5 sm:px-8">
        <div className="flex h-[72px] items-center justify-between gap-3 sm:h-24 lg:h-32">
          <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center" aria-label="The Nordic Vision — home">
            <Logo size="header" />
          </Link>
          <nav className="hidden items-center gap-5 xl:flex" data-testid="desktop-nav" aria-label="Main">
            {NAV_LINKS.map((l) => (l.children ? (
              <NavDropdown key={l.id} item={l} t={t} />
            ) : (
              <NavLink
                key={l.id}
                to={l.path}
                data-testid={`nav-${l.id}-link`}
                className={({ isActive }) =>
                  `whitespace-nowrap py-2 text-[13px] font-medium tracking-wide transition-colors hover:text-[#1477B3] ${
                    isActive ? "text-[#0F5C8C]" : "text-[#41535F]"
                  }`
                }
              >
                {t.nav[l.id]}
              </NavLink>
            )))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-3">
            <LanguageSwitcher className="hidden md:block" />
            <Link
              to="/contact?service=Workforce%20Solutions#enquiry"
              data-testid="header-cta-button"
              className="hidden min-h-[44px] items-center gap-2 whitespace-nowrap bg-gradient-brand px-5 text-sm font-semibold uppercase tracking-wide text-white transition-transform duration-300 hover:-translate-y-0.5 lg:inline-flex"
            >
              {t.cta.requestWorkforce} <ArrowRight size={15} />
            </Link>
            <Link to="/contact" data-testid="mobile-contact-button" aria-label={t.menu.contact} className={iconBtn}>
              <Phone size={21} />
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              className={iconBtn}
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? t.menu.close : t.menu.open}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-x-0 top-full h-[calc(100dvh-4rem)] overflow-y-auto border-t border-[#DDE4EA] bg-[#F4F6F8] sm:h-[calc(100dvh-5rem)] xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="mx-auto flex max-w-[1760px] flex-col px-4 pb-8 pt-2 sm:px-8" aria-label="Mobile">
              {NAV_LINKS.map((l) => (l.children ? (
                <div key={l.id} data-testid={`mobile-nav-${l.id}-group`}>
                  <p className="flex min-h-[52px] items-center border-b border-[#DDE4EA] px-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#5E7280]">{t.nav[l.id]}</p>
                  {l.children.map((c, i) => (
                    <NavLink
                      key={c.id}
                      to={c.path}
                      data-testid={`mobile-nav-${c.id}-link`}
                      className={({ isActive }) =>
                        `flex min-h-[52px] items-center justify-between border-b border-[#DDE4EA] px-1 pl-5 text-base font-medium ${isActive ? "text-[#0F5C8C]" : "text-[#0E1B24]"}`
                      }
                    >
                      <span className="flex items-center gap-3"><span className="font-mono2 text-[11px] text-[#0F5C8C]">{`0${i + 1}`}</span>{t.nav[c.id]}</span>
                      <ChevronRight size={18} className="text-[#5E7280]" />
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={l.id}
                  to={l.path}
                  data-testid={`mobile-nav-${l.id}-link`}
                  className={({ isActive }) =>
                    `flex min-h-[52px] items-center justify-between border-b border-[#DDE4EA] px-1 text-base font-medium ${isActive ? "text-[#0F5C8C]" : "text-[#0E1B24]"}`
                  }
                >
                  {t.nav[l.id]} <ChevronRight size={18} className="text-[#5E7280]" />
                </NavLink>
              )))}
              <div className="mt-6 grid gap-3">
                <Link to="/contact?service=Workforce%20Solutions#enquiry" data-testid="mobile-menu-cta-button" className="inline-flex min-h-[52px] items-center justify-center gap-2 bg-gradient-brand px-5 text-base font-semibold uppercase tracking-wide text-white">
                  {t.cta.requestWorkforce} <ArrowRight size={16} />
                </Link>
                <Link to="/for-candidates#apply" data-testid="mobile-menu-cv-button" className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-[#0E1B24]/70 px-5 text-base font-semibold uppercase tracking-wide text-[#0E1B24]">
                  {t.cta.submitCv}
                </Link>
                <div className="grid grid-cols-2 gap-3">
                  <a href={`tel:+${CONTACT.whatsapp}`} data-testid="mobile-menu-call-button" className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-[#C8D3DB] px-4 text-sm font-semibold text-[#0E1B24]">
                    <Phone size={17} /> {t.hub.callUs}
                  </a>
                  <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp-button" className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-[#25D366]/60 px-4 text-sm font-semibold text-[#25D366]">
                    <WhatsAppIcon size={17} /> WhatsApp
                  </a>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between gap-4">
                <span className="text-sm text-[#5E7280]">{t.common.language}</span>
                <LanguageSwitcher align="right" idPrefix="mobile-" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
